<template lang="pug">
VDialog(
  ref="vDialog"
  v-model="visible"
  width="800"
  attach="#main"
  :close-on-back="false"
  :fullscreen="fullscreen"
  :scrollable="true"
  transition="slide-x-reverse-transition"
  @after-leave="afterLeave"
)
  VCard(:color="theme.current.value.colors.background")
    VCardText.pa-0(data-selector="js-modal-scroller")
      NidCommentPage(
        v-if="!isNaN(nid)"
        ref="nidComment"
        :nid="nid"
        :language="commentLanguage"
      )
        template(#title="{ pageTitle }")
          HH {{ pageTitle }}
    VDivider
    VCardActions
      VBtn.mr-auto(
        variant="outlined"
        data-testid="close-comment-button"
        @click="modalRouter.close"
      )
        VIcon {{ mdiClose }}
        | {{ i18n.t("Global.close") }}
      VBtn.pl-4.pr-4(
        v-if="prevCommentRoute && previousComment"
        v-tooltip:top="previousComment.heading"
        variant="outlined"
        :to="prevCommentRoute"
        data-testid="prev-comment-button"
        @click.prevent="(e: any) => modalLink(e, prevCommentRoute)"
      )
        | {{ i18n.t("TheComment.Previous") }}
      VBtn.pl-4.pr-4(
        v-if="nextCommentRoute && nextComment"
        v-tooltip:top="nextComment.heading"
        variant="elevated"
        :to="nextCommentRoute"
        data-testid="next-comment-button"
        @click.prevent="(e: any) => modalLink(e, nextCommentRoute)"
      )
        | {{ i18n.t("TheComment.Next") }}
</template>

<script setup lang="ts">
import { useDisplay, useGoTo, useTheme } from "vuetify";
import type { RouteLocationResolvedGeneric } from "vue-router";
import NidCommentPage from "~/components/NidCommentPage.vue";
import HH from "~/headings/components/HH.vue";
import type { ProcessedCommentaryComment } from "~/composables/processCommentarySearchResults/types";
import { getCommentLocation } from "~/routing/locations";
import { mdiClose } from "@mdi/js";

type Props = {
  nid: number;
  language: string;
};
const props = defineProps<Props>();

const theme = useTheme();

const vDialog = ref<any>(null);
const nidComment = ref<any>(null);

const position = computed(() => nidComment?.value?.position ?? {});
const previousComment = computed<ProcessedCommentaryComment | undefined>(
  () => position.value.previousResult?.value,
);
const nextComment = computed<ProcessedCommentaryComment | undefined>(
  () => position.value.nextResult?.value,
);

const localeRoute = useLocaleRoute();
const modalRouter = useModalRouter();

const nextCommentRoute = computed(() => {
  const nid = nextComment?.value?.nid;
  const language = nextComment?.value?.language;
  if (nid != null) {
    const lang = i18n.locale.value !== language ? language : undefined;
    return localeRoute(getCommentLocation(nid, lang));
  }
  return undefined;
});

const prevCommentRoute = computed(() => {
  const nid = previousComment?.value?.nid;
  const language = previousComment?.value?.language;

  if (nid != null) {
    const lang = i18n.locale.value !== language ? language : undefined;
    return localeRoute(getCommentLocation(nid, lang));
  }
  return undefined;
});

const i18n = useI18n();
const locale = i18n.locale;

const { xs } = useDisplay();
const fullscreen = computed<boolean>(() => xs.value);

const contentEl = computed(() => vDialog?.value?.contentEl ?? {});
const goTo = useGoTo();
watch(
  () => props.nid,
  (newNid, prevNid) => {
    if (!isNaN(prevNid) && !isNaN(newNid)) {
      // When a new comment is loaded, force scroll top, like on any new page
      goTo("top", {
        easing: "easeInCubic",
        container: contentEl.value.querySelector(
          "[data-selector='js-modal-scroller']",
        ),
      });
    }
  },
);

const visible = ref(true);

function afterLeave() {
  if (modalRouter.stacks.value) {
    modalRouter.close();
  }
}

const commentLanguage = computed<string>(() => props.language || locale.value);
function modalLink(
  e: MouseEvent | TouchEvent,
  to: RouteLocationResolvedGeneric | undefined,
): void {
  if (!to || e.ctrlKey || e.shiftKey || e.metaKey || e.altKey) {
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
  modalRouter.push(to);
}
</script>
